import { format } from 'date-fns';
import { Sparkles } from 'lucide-react';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';
import { formatDuration } from './format';
import type { ConversationAudioProps } from './types';

/**
 * A single user or agent transcript bubble. Hover surfaces the
 * timestamp. When an `audio` companion is provided and the message
 * falls within the recording, clicking the bubble seeks the audio.
 *
 * `isActive` — whether this is the line currently being spoken — is decided
 * by the PARENT, not here. It's an interval question ("is the playhead
 * between this line and the next?") and a bubble can't see its neighbours;
 * the version that guessed from proximity alone lit several bubbles during a
 * fast exchange and none at all across a pause. `activeMessageIdAt` in
 * `audio-sync.ts` owns it now.
 */
export function MessageBubble({
  message,
  audio,
  isActive = false,
  seekMs = null,
  showHoverTimestamp = true,
}: {
  message: InteractionMessageResponse;
  audio?: ConversationAudioProps;
  isActive?: boolean;
  /**
   * Where clicking this bubble starts playback, or null when the call has no
   * anchor. Supplied by the parent (`lineSeekOffsets`) because the target is
   * the PREVIOUS line's end — a timestamp marks the end of its own utterance,
   * so nothing here says where this one began — and a bubble cannot see its
   * neighbours.
   *
   * NOT the time the gutter shows for this row, and across a long wait not
   * close to it. The tooltip below names it for exactly that reason.
   */
  seekMs?: number | null;
  /**
   * Whether to reveal the wall clock beside the bubble on hover. Default true.
   *
   * `ConversationView` passes false when it is drawing a timecode gutter, so
   * the row carries one time rather than two — and they would not even be the
   * same time, the gutter stating the position in the recording.
   */
  showHoverTimestamp?: boolean;
}): React.ReactElement {
  const isAgent = message.kind === 'agent_message';
  const text =
    message.kind === 'user_message' || message.kind === 'agent_message' ? message.text : '';

  const seekable = !!audio?.onSeek && seekMs !== null;

  // Say where the click lands. The gutter labels this row with the line's own
  // (end-of-utterance) timestamp while the click opens at the previous line's
  // end, so after a long wait the two are seconds apart — and a reader who was
  // not told would read the silence that follows as a broken seek.
  //
  // ONLY WHEN THE HOVER TIMESTAMP IS SUPPRESSED, i.e. the same condition that
  // gates it. A row carries ONE time: leaving both on would put a wall clock
  // (`9:41:44 AM`) in the overlay and a recording position (`Play from 0:38`)
  // in the native tooltip — two times in two clocks on one row, which is the
  // exact confusion `showHoverTimestamp` exists to prevent. No consumer wires
  // seek-on-click in hover mode today; this keeps the next one from
  // reintroducing it silently.
  const seekTitle =
    seekMs !== null && seekable && !showHoverTimestamp
      ? `Play from ${formatDuration(seekMs)}`
      : undefined;

  const handleClick = (): void => {
    if (seekMs === null) return;
    audio?.onSeek?.(seekMs);
  };

  // `select-text` opts the bubble back into text selection: native
  // `<button>` elements set `user-select: none`, which otherwise blocks
  // copy/paste of the transcript. When the bubble isn't a seek target
  // (e.g. the post-call detail sheet passes no audio), render a plain
  // `<div>` so it's not a dead disabled button and reads as static text.
  // The active line REPLACES its role colour rather than adding a border to it.
  // A ring alone was too weak to find while listening, and on a user bubble it
  // was a ring around an already-saturated `bg-primary` — near invisible. A
  // filled highlight reads at a glance; who spoke is still carried by the
  // side the bubble sits on and by its avatar, so nothing is lost.
  //
  // Yellow rather than amber: amber already means "tool call" on ToolChip and
  // "warning" on the connection banner in this same view. This is a much more
  // saturated fill than either, but the hue is deliberately the one nobody
  // reads as a status.
  //
  // These come LAST in the `cn` so tailwind-merge drops the role `bg-*`/`text-*`.
  const bubbleClassName = cn(
    'relative max-w-[75%] select-text rounded-lg px-3 py-1.5 text-left text-sm transition-all',
    isAgent ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground',
    seekable ? 'cursor-pointer hover:ring-2 hover:ring-ring/40' : 'cursor-default',
    isActive &&
      'bg-yellow-200 text-yellow-950 shadow-sm ring-2 ring-yellow-500 dark:bg-yellow-400/30 dark:text-yellow-50 dark:ring-yellow-400'
  );

  const timestamp = !showHoverTimestamp ? null : (
    <span
      className={cn(
        'absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100',
        isAgent ? 'left-full ml-2' : 'right-full mr-2'
      )}
    >
      {format(new Date(message.timestamp), 'h:mm:ss a')}
    </span>
  );

  return (
    // `data-active-message` is the hook ConversationView scrolls to during
    // playback — a data attribute rather than a ref so the parent doesn't
    // have to hold one ref per message in a transcript of arbitrary length.
    <div
      data-active-message={isActive ? 'true' : undefined}
      className={cn('group flex items-start gap-2', isAgent ? 'justify-start' : 'justify-end')}
    >
      {isAgent && (
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
          <Sparkles className="h-3.5 w-3.5 text-primary-foreground" />
        </div>
      )}
      {seekable ? (
        <button type="button" onClick={handleClick} title={seekTitle} className={bubbleClassName}>
          {text}
          {timestamp}
        </button>
      ) : (
        <div className={bubbleClassName}>
          {text}
          {timestamp}
        </div>
      )}
      {!isAgent && (
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600">
          <span className="text-[10px] font-medium text-white">You</span>
        </div>
      )}
    </div>
  );
}
